import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MobilePaymentMethodChooser from '../components/MobilePaymentMethodChooser';
import PaymentUpiQrCode from '../components/PaymentUpiQrCode';
import { PAYMENT_BANK_DETAILS, PAYMENT_MERCHANT } from '../config/paymentMerchant';
import { useUpiPaymentLauncher } from '../hooks/useUpiPaymentLauncher';
import { ensurePaymentAttemptSession } from '../utils/paymentAttemptApi';

const PLAN_STYLE = {
  emi: {
    summary: 'Pay for the current month EMI via bank transfer.',
    accent: 'from-violet-500 to-indigo-500',
    shadow: 'shadow-indigo-300/60',
  },
  full: {
    summary: 'Pay the full ₹5,000 in one bank transfer (same as all 10 monthly payments combined).',
    accent: 'from-cyan-500 to-teal-500',
    shadow: 'shadow-cyan-300/60',
  },
};

const PAYMENT_TABS = [
  { id: 'qr', label: 'QR Code' },
  { id: 'bank', label: 'Bank Details' },
];

function PaymentQrScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('qr');
  const [copiedField, setCopiedField] = useState(null);
  const selectedPlanId = location.state?.selectedPlan || 'emi';
  const planName = location.state?.planName;
  const amountLabel = location.state?.amountLabel;

  const style = PLAN_STYLE[selectedPlanId] || PLAN_STYLE.emi;
  const displayPlanName =
    planName ||
    (selectedPlanId === 'full' ? 'Full Settlement in 1st Month' : 'Monthly EMI Plan');
  const displayAmount = amountLabel || (selectedPlanId === 'full' ? '₹5,000' : '₹500');
  const upiLauncher = useUpiPaymentLauncher({
    displayAmount,
    planName: displayPlanName,
  });
  const { supportsUpiPay, showUploadReminder } = upiLauncher;

  const goToUploadProof = () =>
    navigate('/payment/upload', {
      state: {
        selectedPlan: selectedPlanId,
        planName: displayPlanName,
        amountLabel: displayAmount,
        catalogPlanId: location.state?.catalogPlanId,
      },
    });

  useEffect(() => {
    ensurePaymentAttemptSession({
      selectedPlan: selectedPlanId,
      planName: displayPlanName,
      amountLabel: displayAmount,
    }).catch(() => {
      /* audit session is best-effort; payment flow must continue */
    });
  }, [selectedPlanId, displayPlanName, displayAmount]);
  const summary = amountLabel
    ? `Pay ${amountLabel} using the QR code or bank details below.`
    : selectedPlanId === 'full'
      ? 'Pay the full amount using the QR code or bank transfer details below.'
      : 'Pay your monthly EMI using the QR code or bank transfer details below.';

  const copyValue = (label, value) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopiedField(label);
      window.setTimeout(() => setCopiedField(null), 2000);
    });
  };

  return (
    <div className="w-full min-h-[calc(100vh-4rem)] overflow-hidden bg-gradient-to-br from-slate-100 via-indigo-50 to-cyan-100 px-4 py-3 sm:px-5 sm:py-4 flex items-center">
      <div className="max-w-5xl mx-auto w-full">
        <div className="rounded-3xl bg-white border border-slate-200/80 p-5 sm:p-6 shadow-[0_12px_40px_-8px_rgba(15,23,42,0.12),0_4px_16px_-4px_rgba(15,23,42,0.08)]">
          <p className="text-[11px] font-bold tracking-[0.2em] text-indigo-600 uppercase mb-2">
            Secure Payment
          </p>
          <h1
            className="text-2xl sm:text-3xl font-black text-slate-900 mb-1.5 leading-tight"
            style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
          >
            Complete Your Payment
          </h1>
          <p className="text-sm text-slate-600 mb-4 leading-snug">{summary}</p>

          <div
            className={`mb-4 rounded-xl border px-4 py-3 ${
              selectedPlanId === 'full'
                ? 'border-cyan-200/90 bg-cyan-50/70'
                : 'border-violet-200/90 bg-violet-50/70'
            }`}
          >
            <p className="text-xs font-semibold text-slate-800">
              {selectedPlanId === 'full'
                ? 'Full settlement: transfer the complete ₹5,000 in one payment before uploading proof.'
                : 'Monthly EMI: transfer only ₹500 for this month before uploading proof.'}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-[11px] font-semibold text-slate-500 mb-1">Selected Plan</p>
              <h2 className="text-base font-black text-slate-900">{displayPlanName}</h2>
              <p className="text-xs text-slate-500 mb-2">Payable Amount</p>
              <p className="text-3xl font-black text-slate-900 mb-4">{displayAmount}</p>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="rounded-xl bg-white border border-slate-200 p-1 mb-4">
                  <div className="relative grid grid-cols-2">
                    <span
                      className={`absolute top-0 bottom-0 w-1/2 rounded-lg bg-gradient-to-r from-violet-500 to-indigo-500 shadow-md shadow-indigo-300/50 transition-transform duration-300 ${
                        activeTab === 'qr' ? 'translate-x-0' : 'translate-x-full'
                      }`}
                    />
                    {PAYMENT_TABS.map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        className={`relative z-10 rounded-lg px-3 py-2 text-xs sm:text-sm font-bold transition-colors ${
                          activeTab === tab.id ? 'text-white' : 'text-slate-600'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>

                {activeTab === 'qr' ? (
                  supportsUpiPay ? (
                    <MobilePaymentMethodChooser
                      displayAmount={displayAmount}
                      planName={displayPlanName}
                      launcher={upiLauncher}
                    />
                  ) : (
                    <div className="space-y-3">
                      <div className="text-center">
                        <p className="text-[11px] font-bold uppercase tracking-widest text-indigo-600">
                          Pay with any UPI app
                        </p>
                        <p className="mt-1 text-xs text-slate-600 leading-snug">
                          Download the QR, open GPay / PhonePe on your phone → Scan →{' '}
                          <strong>Gallery</strong> → select the image. Or scan this QR with your
                          phone camera.
                        </p>
                      </div>

                      <PaymentUpiQrCode
                        displayAmount={displayAmount}
                        planName={displayPlanName}
                        size={224}
                        showAmountHint={false}
                        showDownload
                        downloadButtonLabel="Download QR to pay with any UPI app"
                        downloadButtonPrimary
                      />

                      <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-left">
                        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1.5">
                          Quick steps
                        </p>
                        <ol className="space-y-1 text-xs text-slate-700 leading-snug list-decimal pl-4">
                          <li>
                            Tap <strong>Download QR to pay with any UPI app</strong>.
                          </li>
                          <li>
                            On your phone open <strong>GPay / PhonePe / Paytm</strong> → Scan →{' '}
                            <strong>Gallery</strong> → choose the saved QR.
                          </li>
                          <li>
                            Pay <strong>{displayAmount}</strong> to{' '}
                            <strong>{PAYMENT_MERCHANT.payeeName}</strong>, then upload proof.
                          </li>
                        </ol>
                      </div>
                    </div>
                  )
                ) : (                  <>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-white text-sm">
                        🏦
                      </div>
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-widest text-indigo-600">
                          Bank Details
                        </p>
                        <p className="text-xs text-slate-500">Use these details for NEFT / IMPS / RTGS</p>
                      </div>
                    </div>

                    <div className="space-y-2.5">
                      {PAYMENT_BANK_DETAILS.map((item) => (
                        <div
                          key={item.label}
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2.5"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                                {item.label}
                              </p>
                              <p
                                className={`mt-0.5 text-sm font-semibold text-slate-900 break-all ${
                                  item.mono ? 'font-mono tracking-wide' : ''
                                }`}
                              >
                                {item.value}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => copyValue(item.label, item.value)}
                              className="flex-shrink-0 rounded-lg border border-slate-200 px-2.5 py-1 text-[10px] font-bold text-slate-600 transition-colors hover:bg-slate-50"
                            >
                              {copiedField === item.label ? 'Copied!' : 'Copy'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="text-base font-black text-slate-900 mb-2.5">How to Pay</h3>
              <ol className="space-y-2 text-sm text-slate-600 leading-snug">
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-slate-900 text-[11px] font-black text-white">
                    1
                  </span>
                  <span>
                    Tap <strong>Download QR to pay with any UPI app</strong>, then open GPay /
                    PhonePe on your phone → Scan → <strong>Gallery</strong> → select the saved QR.
                    You can also scan the QR directly or pay via{' '}
                    <strong>NEFT / IMPS / RTGS</strong> under Bank Details.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-slate-900 text-[11px] font-black text-white">
                    2
                  </span>
                  <span>
                    Confirm amount <strong>{displayAmount}</strong> and payee{' '}
                    <strong>{PAYMENT_MERCHANT.payeeName}</strong>, then complete payment.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-slate-900 text-[11px] font-black text-white">
                    3
                  </span>
                  <span>
                    Save a screenshot/PDF with the transaction ID, then tap{' '}
                    <strong>Proceed to Upload Payment Proof</strong>.
                  </span>
                </li>
              </ol>

              <div className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
                <p className="text-xs text-amber-900 leading-snug">
                  Admin will verify your proof and update your account. Keep the transaction ID
                  visible in the upload.
                </p>
              </div>

              {supportsUpiPay && showUploadReminder && (
                <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                  <p className="text-xs text-emerald-900 leading-snug font-semibold">
                    If you&apos;ve completed your payment, please upload your payment proof for
                    verification.
                  </p>
                </div>
              )}

              <button
                type="button"
                onClick={goToUploadProof}
                className={`mt-3 w-full rounded-xl px-4 py-2.5 text-sm font-extrabold text-white bg-gradient-to-r ${style.accent} shadow-lg ${style.shadow} transition-all active:scale-[0.99]`}
              >
                Proceed to Upload Payment Proof
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentQrScreen;
